import React, { type ComponentProps } from 'react';
import { useWindowDimensions } from 'react-native';
import {
  Button,
  ButtonText,
  Toast,
  ToastDescription,
  ToastTitle,
  VStack,
} from '@gluestack-ui/themed';

type ToastAction = 'error' | 'success' | 'warning' | 'info' | 'attention';
type ToastVariant = 'outline' | 'solid' | 'accent';
type ToastMarginTop = ComponentProps<typeof Toast>['mt'];

export type AppToastCta = {
  label: string;
  onPress: () => void;
  testID?: string;
};

export type AppToastProps = {
  id: string;
  action: ToastAction;
  message: string;
  title?: string;
  variant?: ToastVariant;
  pointerEvents?: 'box-none' | 'auto' | 'none';
  mt?: ToastMarginTop;
  /** Renders message as a single truncated title (portal-safe helper pattern). */
  titleOnly?: boolean;
  ctaLabel?: string;
  ctaTestID?: string;
  onCtaPress?: () => void;
};

/**
 * AppToast
 * ----
 * Shared toast layout: hug short copy, grow to min(560px, viewport − 32),
 * hidden overflow, and clamped title/description text.
 */
export function AppToast({
  id,
  action,
  message,
  title,
  variant = 'accent',
  pointerEvents = 'box-none',
  mt,
  titleOnly = false,
  ctaLabel,
  ctaTestID,
  onCtaPress,
}: AppToastProps) {
  const { width } = useWindowDimensions();
  const maxWidth = Math.min(560, width - 32);
  const pointerEventsValue = onCtaPress ? 'auto' : pointerEvents;

  if (titleOnly) {
    return (
      <Toast
        nativeID={`toast-${id}`}
        action={action}
        variant={variant}
        pointerEvents={pointerEventsValue}
        mt={mt}
        maxWidth={maxWidth}
        overflow="hidden"
      >
        <VStack flex={1} minWidth={0} maxWidth="100%" space="xs">
          <ToastTitle isTruncated>{message}</ToastTitle>
        </VStack>
      </Toast>
    );
  }

  return (
    <Toast
      nativeID={`toast-${id}`}
      action={action}
      variant={variant}
      pointerEvents={pointerEventsValue}
      mt={mt}
      maxWidth={maxWidth}
      overflow="hidden"
    >
      <VStack flex={1} minWidth={0} maxWidth="100%" space="xs">
        {!!title && <ToastTitle isTruncated>{title}</ToastTitle>}
        <ToastDescription numberOfLines={2}>{message}</ToastDescription>
        {ctaLabel && onCtaPress ? (
          <Button
            size="sm"
            variant="link"
            alignSelf="flex-start"
            testID={ctaTestID}
            onPress={onCtaPress}
          >
            <ButtonText>{ctaLabel}</ButtonText>
          </Button>
        ) : null}
      </VStack>
    </Toast>
  );
}
